import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { useParams, useNavigate } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import Papa from "papaparse";
import CreateBulkTagComponent from "./component";

const CreateBulkTagContainer = (props) => {
  const { corporateId } = useParams();
  const navigate = useNavigate();
  
  const [currentView, setCurrentView] = useState('upload');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadedData, setUploadedData] = useState(null);
  const [selectedTags, setSelectedTags] = useState({});
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [activateLoading, setActivateLoading] = useState(false);
  const [showActivateSuccessPopup, setShowActivateSuccessPopup] = useState(false);
  const [activateResult, setActivateResult] = useState(null);
  const [csvOriginalData, setCsvOriginalData] = useState({});
  const [bulkUploadHeading, setBulkUploadHeading] = useState("Upload Tag in bulk");

  // Handle CSV file upload as FormData
  const handleFileUpload = (file) => {
    if (!file) return;
    
    setUploading(true);
    setUploadProgress(0);
    setCurrentView('upload');
    
    // First parse CSV to get original department_id
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const csvData = results.data;
        const originalDeptMap = {};
        
        csvData.forEach(row => {
          const uid = row.uid?.trim();
          const deptId = row.department_id?.trim();
          if (uid && deptId) {
            originalDeptMap[uid] = parseInt(deptId);
          }
        });
        
        setCsvOriginalData(originalDeptMap);
        console.log("Original CSV Department Map:", originalDeptMap);
        
        // Now upload file
        const formData = new FormData();
        formData.append('file', file);
        
        const interval = setInterval(() => {
          setUploadProgress(prev => Math.min(prev + 10, 90));
        }, 200);
        
        props.getDataFromAPI(
          `/partner/api/v2/corporates/${corporateId}/tag/bulk`,
          "POST",
          formData,
          (response) => {
            clearInterval(interval);
            setUploadProgress(100);
            
            console.log("API Success Response:", response);
            
            const apiData = response?.data || response || [];
            
            if (apiData.length === 0) {
              toastr.warning("Warning", "No tags were created");
              setUploading(false);
              setCurrentView('upload');
              return;
            }
            
            // Group by department using original CSV department_id
            const groupedByDept = {};
            apiData.forEach(tag => {
              const uid = tag.uid;
              let deptId = originalDeptMap[uid];
              
              if (!deptId) {
                deptId = tag.department_id || tag.departmentId;
              }
              
              // Handle department_id 0 - keep it as 0
              if (deptId === undefined || deptId === null) {
                deptId = 0;
              }
              
              const deptName = tag.department || tag.department_name || `Department ${deptId}`;
              
              if (!groupedByDept[deptId]) {
                groupedByDept[deptId] = {
                  department_id: deptId,
                  department_name: deptName,
                  tags: []
                };
              }
              
              groupedByDept[deptId].tags.push({
                id: tag.id,
                uid: tag.uid,
                token: tag.token,
                status: tag.status || "new",
                balance: tag.balance,
                department_id: deptId,
                department_name: deptName
              });
            });
            
            const successData = Object.values(groupedByDept);
            setUploadedData(successData);
            setSelectedTags({});
            setUploadResult({
              success: true,
              message: `${apiData.length} tags uploaded successfully`,
              count: apiData.length
            });
            setShowSuccessPopup(true);
            setUploading(false);
          },
          (err) => {
            clearInterval(interval);
            console.log("API Error Response:", err);
            setUploadProgress(0);
            setShowSuccessPopup(false);
            setUploadResult({
              success: false,
              message: err?.message || err?.error 
            });
            setUploading(false);
            toastr.error("Error", err?.message );
          }
        );
      },
      error: (error) => {
        console.error("CSV Parse Error:", error);
        toastr.error("Error", "Failed to parse CSV file");
        setUploading(false);
      }
    });
  };

  // Handle file select - change heading
  const handleFileSelect = (file) => {
    if (!file) return;
    if (file.type !== "text/csv" && !file.name.endsWith('.csv')) {
      toastr.error("Error", "Please upload a CSV file only");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toastr.error("Error", "File size should be less than 5MB");
      return;
    }
    setBulkUploadHeading("Uploaded");
  };

  // Handle cancel file - reset heading
  const handleCancelFile = () => {
    setBulkUploadHeading("Upload Tag in bulk");
  };

  // Handle tag selection
  const handleToggleTagSelection = (tags, action) => {
    if (action === 'clear') {
      setSelectedTags({});
    } else if (action === 'all') {
      setSelectedTags(tags);
    } else if (action === 'single') {
      setSelectedTags(prev => {
        const newSelected = { ...prev };
        const tagId = Object.keys(tags)[0];
        if (newSelected[tagId]) {
          delete newSelected[tagId];
        } else {
          newSelected[tagId] = tags[tagId];
        }
        return newSelected;
      });
    } else if (action === 'department') {
      setSelectedTags(prev => {
        const newSelected = { ...prev };
        const tagIds = Object.keys(tags);
        const allSelected = tagIds.every(id => prev[id]);
        
        if (allSelected) {
          tagIds.forEach(id => delete newSelected[id]);
        } else {
          tagIds.forEach(id => {
            newSelected[id] = tags[id];
          });
        }
        return newSelected;
      });
    }
  };

  // Group selected tags by department for activate API - Handle department_id 0
  const groupTagsByDepartment = (selectedTagsList) => {
    const grouped = {};
    
    Object.values(selectedTagsList).forEach(tag => {
      let deptId = tag.department_id;
      
      if (deptId === undefined || deptId === null) {
        deptId = csvOriginalData[tag.uid];
      }
      
      if (deptId === undefined || deptId === null) {
        deptId = 0;
      }
      
      if (typeof deptId === 'string' && !isNaN(parseInt(deptId))) {
        deptId = parseInt(deptId);
      }
      
      if (!grouped[deptId]) {
        grouped[deptId] = {
          departmentId: deptId,
          uids: []
        };
      }
      
      const uid = tag.uid || tag.tagId;
      if (uid && !grouped[deptId].uids.includes(uid)) {
        grouped[deptId].uids.push(uid);
      }
    });
    
    const result = Object.values(grouped);
    console.log("Activate Request Body (Including Department ID 0):", JSON.stringify(result, null, 2));
    return result;
  };

  // Handle activate selected tags
  const handleActivateSelectedTags = (selectedTagsList) => {
    const tagIds = Object.keys(selectedTagsList);
    if (tagIds.length === 0) {
      toastr.warning("No Selection", "Please select tags to activate");
      return;
    }
    
    setActivateLoading(true);
    
    const requestBody = groupTagsByDepartment(selectedTagsList);
    
    console.log("=== ACTIVATE API REQUEST ===");
    console.log("URL:", `/partner/api/v2/corporates/${corporateId}/tag/bulk/activate`);
    console.log("Payload:", JSON.stringify(requestBody, null, 2));
    
    if (requestBody.length === 0) {
      toastr.error("Error", "Unable to process tags. No valid department IDs found.");
      setActivateLoading(false);
      return;
    }
   
    props.getDataFromAPI(
      `/partner/api/v2/corporates/${corporateId}/tag/bulk/activate`,
      "PUT",
      requestBody,
      (response) => {
        setActivateLoading(false);
        
        const data = response?.data || response;
        const successCount = data?.success_count || tagIds.length;
        const failureCount = data?.failure_count || 0;
        
        setActivateResult({
          success: true,
          successCount: successCount,
          failureCount: failureCount,
          totalCount: tagIds.length,
          failedDetails: data?.failed_details || []
        });
        setShowActivateSuccessPopup(true);
        
        if (successCount > 0) {
          toastr.success("Success", `${successCount} tags activated successfully`);
        }
        if (failureCount > 0) {
          toastr.warning("Warning", `${failureCount} tags failed to activate`);
        }
      },
      (err) => {
        setActivateLoading(false);
        setActivateResult({
          success: false,
          message: err?.message || err?.error || "Failed to activate tags",
          totalCount: tagIds.length
        });
        setShowActivateSuccessPopup(true);
        toastr.error("Error", err?.message || "Failed to activate tags");
      }
    );
  };

  // Download sample CSV
  const handleDownloadSample = () => {
    const sampleData = [
      { department_id: 8, uid: "9000001000", token: "TKN00000X" },
      { department_id: 8, uid: "9000001001", token: "TKN00001X" },
      { department_id: 8, uid: "9000001002", token: "TKN00002X" }
    ];
    
    const csv = Papa.unparse(sampleData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk_tags_sample.csv";
    a.click();
    URL.revokeObjectURL(url);
    toastr.success("Download Started", "Sample CSV file downloaded");
  };

  const handleBackClick = () => {
    setCurrentView('upload');
    setUploadedData(null);
    setUploadResult(null);
    setSelectedTags({});
    setShowSuccessPopup(false);
    setBulkUploadHeading("Upload Tag in bulk");
    navigate(-1);
  };

  const handleAddMoreTags = () => {
    setCurrentView('upload');
    setUploadedData(null);
    setUploadResult(null);
    setSelectedTags({});
    setShowSuccessPopup(false);
    setBulkUploadHeading("Upload Tag in bulk");
  };

  const handleGoToDashboard = () => {
    navigate(`/home/walletandtags/tagpay-dashboard/${corporateId}`);
  };

  const handleRetry = () => {
    setCurrentView('upload');
    setUploadResult(null);
    setUploadedData(null);
    setSelectedTags({});
    setShowSuccessPopup(false);
    setBulkUploadHeading("Upload Tag in bulk");
  };

  const handlePopupOk = () => {
    setShowSuccessPopup(false);
    setCurrentView('success');
  };

  const handleCloseActivatePopup = () => {
    setShowActivateSuccessPopup(false);
    setActivateResult(null);
    navigate(`/home/walletandtags/tagpay-dashboard/${corporateId}`);
  };

  return (
    <CreateBulkTagComponent
      corporateId={corporateId}
      currentView={currentView}
      uploading={uploading}
      uploadProgress={uploadProgress}
      uploadResult={uploadResult}
      uploadedData={uploadedData}
      selectedTags={selectedTags}
      showSuccessPopup={showSuccessPopup}
      activateLoading={activateLoading}
      showActivateSuccessPopup={showActivateSuccessPopup}
      activateResult={activateResult}
      bulkUploadHeading={bulkUploadHeading}
      onFileUpload={handleFileUpload}
      onFileSelect={handleFileSelect}
      onCancelFile={handleCancelFile}
      onBackClick={handleBackClick}
      onDownloadSample={handleDownloadSample}
      onAddMoreTags={handleAddMoreTags}
      onGoToDashboard={handleGoToDashboard}
      onRetry={handleRetry}
      onToggleTagSelection={handleToggleTagSelection}
      onActivateSelectedTags={handleActivateSelectedTags}
      onPopupOk={handlePopupOk}
      onCloseActivatePopup={handleCloseActivatePopup}
    />
  );
};

function mapStateToProps(state) {
  return {
    props: state,
  };
}

export default connect(mapStateToProps, {
  getDataFromAPI,
})(CreateBulkTagContainer);